<template>
  <div>
    <table
      class="table table-sm table-bordered mt-3 realization-scopes-summary"
    >
      <thead>
        <tr>
          <th colspan="4" class="text-center">
            {{ entityNamePlural.toUpperCase() }}
          </th>
        </tr>
        <tr>
          <th class="number-column">N°</th>
          <th class="w-25">{{ $t("reusable.common.name") }}</th>
          <th>{{ $t("reusable.common.description") }}</th>
          <th class="amount-column">
            {{
              $getVisibleNames("mesh.egressprofilematter", true, "Asignaturas")
            }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in summary" :key="row.realization_scope.id">
          <td class="text-center">
            <span class="realization-scope-number">{{
              row.realization_scope.number
            }}</span>
          </td>
          <td class="text-left">{{ row.realization_scope.name }}</td>
          <td class="text-left description-cell">
            {{ row.realization_scope.description }}
          </td>
          <td class="text-center">
            <!-- Igual que la cantidad de asignaturas por competencia (Matrix3View): el
                 listado, que puede ser largo, va en un modal -->
            <button
              type="button"
              class="btn btn-secondary btn-sm popover-btn"
              :disabled="row.matters.length == 0"
              :title="
                row.matters.length > 0
                  ? $t('matrix3.matrix3View.viewDetails')
                  : ''
              "
              @click="openMatters(row)"
            >
              <span class="badge badge-secondary">{{
                row.matters.length
              }}</span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <BsModal
      v-model="show_matters"
      :title="
        selected_row
          ? `${selected_row.realization_scope.number}. ${selected_row.realization_scope.name}`
          : ''
      "
      size="lg"
    >
      <table v-if="selected_row" class="table table-sm table-bordered mb-0">
        <thead>
          <tr>
            <th>{{ $t("matrix3.matrix3View.planLevel") }}</th>
            <th>
              {{
                $getVisibleNames(
                  "mesh.egressprofilematter",
                  true,
                  "Asignaturas"
                )
              }}
            </th>
            <th>
              {{
                $getVisibleNames(
                  "manual.credits",
                  true,
                  "Créditos SCT Totales"
                )
              }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="egress_profile_matter in selected_row.matters"
            :key="egress_profile_matter.id"
          >
            <td class="text-center">{{ egress_profile_matter.plan_level }}</td>
            <td class="text-left">
              <router-link
                :to="{
                  name: 'MatterProgramView',
                  params: { matter_id: egress_profile_matter.id },
                }"
                :title="`${$t('matrix3.matrix3View.goTo')} ${$getVisibleNames(
                  'manual.matter_program',
                  false,
                  'Programa de Asignatura'
                )}`"
              >
                {{ matterName(egress_profile_matter).toUpperCase() }}
              </router-link>
            </td>
            <td class="text-center">
              {{ matterCredits(egress_profile_matter) }}
            </td>
          </tr>
        </tbody>
      </table>
    </BsModal>
  </div>
</template>

<script setup>
// eslint-plugin-vue 6.x (anterior a <script setup>) no ve que estos bindings
// se usan en el template. Quitar el disable cuando se actualice el tooling.
/* eslint-disable no-unused-vars */
// Resumen de la Matriz 3: Ámbitos de realización del perfil de egreso, con el número con
// que aparecen en la matriz, y las asignaturas que tributan a cada uno. Matrix3 solo lo
// muestra si el perfil tiene ámbitos (y los carga).
import { computed, getCurrentInstance, ref } from "vue";
import * as names from "@/store/names";
import { scopesOfMatter } from "@/utils/realizationScopes";
import BsModal from "@/components/reusable/bs/BsModal";

const props = defineProps({
  egress_profile_id: {
    type: Number,
    required: true,
  },
  // Ámbitos del perfil con su number (ver Matrix3.profileRealizationScopes)
  realization_scopes: {
    type: Array,
    default: () => [],
  },
  // Asignaturas del perfil, en el orden en que se muestran en la matriz
  egress_profile_matters: {
    type: Array,
    default: () => [],
  },
});

const proxy = getCurrentInstance().proxy;
const store = proxy.$store;

const entityNamePlural = computed(() =>
  proxy.$getVisibleNames(
    "mesh.egressprofilerealizationscope",
    true,
    "Ámbitos de realización"
  )
);

const summary = computed(() => {
  const profile_competences = store.getters[names.PROFILE_COMPETENCES].filter(
    (x) => x.egress_profile == props.egress_profile_id
  );
  const scope_ids_by_matter = new Map(
    props.egress_profile_matters.map((egress_profile_matter) => [
      egress_profile_matter.id,
      scopesOfMatter(
        props.realization_scopes,
        profile_competences,
        egress_profile_matter
      ).map((x) => x.id),
    ])
  );
  return props.realization_scopes.map((realization_scope) => ({
    realization_scope,
    matters: props.egress_profile_matters.filter((x) =>
      scope_ids_by_matter.get(x.id).includes(realization_scope.id)
    ),
  }));
});

const show_matters = ref(false);
const selected_row = ref(null);

function openMatters(row) {
  selected_row.value = row;
  show_matters.value = true;
}

function findMatter(egress_profile_matter) {
  return (
    store.getters[names.MATTERS].find(
      (x) => x.id == egress_profile_matter.matter
    ) || {}
  );
}

function matterName(egress_profile_matter) {
  return (
    egress_profile_matter.matter__name ||
    findMatter(egress_profile_matter).name ||
    ""
  );
}

// Mismo criterio que el listado por competencia en Matrix3View
function matterCredits(egress_profile_matter) {
  return egress_profile_matter.use_matter_values
    ? findMatter(egress_profile_matter).credits || 0
    : egress_profile_matter.credits;
}
</script>

<style scoped>
th {
  vertical-align: middle;
  background-color: var(--kl-color-accent-medium) !important;
  color: var(--kl-text-on-primary) !important;
  font-size: var(--secondary-font-size);
  text-align: center;
}
td {
  vertical-align: middle;
  font-size: var(--secondary-font-size);
}
.description-cell {
  white-space: pre-line;
}
.number-column {
  width: 1%;
}
.amount-column {
  width: 10%;
}
.realization-scope-number {
  display: inline-block;
  min-width: 1.4em;
  padding: 0 0.3em;
  border-radius: var(--kl-radius-pill);
  background-color: var(--kl-chip-bg);
  color: var(--kl-chip-text);
  border: 1px solid var(--kl-chip-border);
  text-align: center;
}
.popover-btn .badge {
  border: none !important;
}
</style>
